import { css, html, LitElement, type PropertyValues } from 'lit'
import { property, customElement } from 'lit/decorators.js'

enum Direction {
    Forward = 'Forward',
    Backward = 'Backward',
}

@customElement('zaptic-dots-overview')
export class DotsOverview extends LitElement {
    static styles = css`
        :host {
            display: flex;
            position: relative;
            /* default to size of image */
            width: 100%;
            /* center image if this component doesn't match the image aspect ratio */
            justify-content: center;
            align-items: center;
            --circle-diameter: 8px;
            --circle-diameter-small: calc(var(--circle-diameter) / 1.5);
            --circle-diameter-smallest: calc(var(--circle-diameter) / 2.5);
            --circle-margin: 5px;
            --circle-color: #b2c2db;
            --circle-color-active: #794bff;
        }

        .circle {
            height: var(--circle-diameter);
            width: var(--circle-diameter);
            border-radius: 50%;
            background-color: var(--circle-color);
        }

        .circle:not(:first-of-type) {
            margin-left: var(--circle-margin);
        }

        .circle.active {
            background-color: var(--circle-color-active);
        }

        .circle.small {
            height: var(--circle-diameter-small);
            width: var(--circle-diameter-small);
        }

        .circle.smallest {
            height: var(--circle-diameter-smallest);
            width: var(--circle-diameter-smallest);
        }
    `

    @property({ type: Number })
    dots: number = 0

    @property({ type: Number })
    active: number = 0

    private frame: {
        size: number
        left: number
        right: number
        active: number
        direction: Direction
    } = {
        size: 0,
        left: 0,
        right: 0,
        active: 0,
        direction: Direction.Forward,
    }

    private computeFrame() {
        if (!this.dots) {
            return
        }
        // frame size
        this.frame.size = this.dots <= 3 ? this.dots : 3

        // active
        const newActive = this.computeActiveDot()

        // aux dots
        this.computeAuxDots(newActive)

        // active
        this.frame.active = newActive
    }

    private computeActiveDot() {
        let newActive

        if (this.active === 0) {
            newActive = 0
        } else if (this.active === this.dots - 1 && this.dots >= 3) {
            newActive = 2
        } else if (this.frame.direction === Direction.Forward) {
            newActive = this.frame.active < 2 ? this.frame.active + 1 : this.frame.active
        } else {
            newActive = this.frame.active > 0 ? this.frame.active - 1 : this.frame.active
        }
        return newActive
    }

    private computeAuxDots(newActive: number) {
        if (this.frame.direction === Direction.Forward) {
            if (newActive <= this.frame.active) {
                this.frame.right = this.dots > 3 ? Math.max(0, Math.min(2, this.dots - 1 - this.active)) : 0
                this.frame.left = this.dots > 3 ? Math.max(0, Math.min(2, this.active + 1 - 3)) : 0
            }
        } else {
            if (newActive >= this.frame.active) {
                this.frame.right = this.dots > 3 ? Math.max(0, Math.min(2, this.dots - 3 - this.active)) : 0
                this.frame.left = this.dots > 3 ? Math.max(0, Math.min(2, this.active)) : 0
            }
        }
    }

    private renderDots() {
        this.computeFrame()

        const auxLeft =
            this.frame.left > 0
                ? html`${[...Array(this.frame.left).keys()].map(
                      (k) => html` <div class="circle small${this.frame.left === 2 && k < 1 ? 'est' : ''}"></div> `
                  )}`
                : html``
        const auxRight =
            this.frame.right > 0
                ? html`${[...Array(this.frame.right).keys()].map(
                      (k) => html` <div class="circle small${this.frame.right === 2 && k > 0 ? 'est' : ''}"></div> `
                  )}`
                : html``

        return html`
            ${auxLeft}
            ${this.dots > 1
                ? [...Array(this.frame.size).keys()].map(
                      (k) => html` <div class="circle${k === this.frame.active ? ' active' : ''}"></div> `
                  )
                : html``}
            ${auxRight}
        `
    }

    willUpdate(changedProperties: PropertyValues<DotsOverview>) {
        if (changedProperties.has('active')) {
            const oldValue = changedProperties.get('active')
            const newValue = this.active

            if (oldValue != null && newValue != null) {
                if (oldValue < newValue) {
                    this.frame.direction = Direction.Forward
                } else {
                    this.frame.direction = Direction.Backward
                }
            } else {
                this.frame.direction = Direction.Forward
            }
        }
    }

    render() {
        const dots = this.renderDots()
        return html`${dots}`
    }
}
